<script setup>
import { computed } from 'vue';
import HubBadge from './HubBadge.vue';
import XlTooltip from '../xl/XlTooltip.vue';
import HubNavIcon from './HubNavIcon.vue';

const props = defineProps({
  item: { type: Object, required: true },
  active: { type: Boolean, default: false },
  collapsed: { type: Boolean, default: false },
  count: { type: Number, default: 0 },
});

defineEmits(['select']);

// A disabled item is a signpost to something not built yet. It stays visible
// so the shape of the product is legible, but it is a <span>, not a button:
// nothing to tab to, nothing to click, and screen readers are told plainly.
const isDisabled = computed(() => props.item.disabled || !props.item.to);

// One that says why ("Insights aren't available yet") says so on
// hover, on focus and on a tap: it is a button that goes nowhere, so a
// keyboard can reach it and a phone has something to tap, and the reason is
// its description. A tap focuses it, because a touch does not focus a button
// on its own (Safari) and the message opens on focus.
const soon = computed(() => (isDisabled.value ? props.item.soon || '' : ''));
const tell = (event) => event.currentTarget.focus();
</script>

<template>
  <XlTooltip v-if="soon" :text="soon" placement="top" control class="hub-nav-item__tip">
    <button
      type="button"
      class="hub-nav-item hub-nav-item--disabled"
      :class="{ 'hub-nav-item--collapsed': collapsed }"
      aria-disabled="true"
      data-testid="hub-nav-soon"
      @click="tell"
    >
      <span class="hub-nav-item__icon">
        <HubNavIcon :name="item.icon" />
      </span>
      <span class="hub-nav-item__label">{{ item.label }}</span>
    </button>
  </XlTooltip>
  <component
    v-else
    :is="isDisabled ? 'span' : 'button'"
    class="hub-nav-item"
    :class="{ 'hub-nav-item--active': active, 'hub-nav-item--disabled': isDisabled, 'hub-nav-item--collapsed': collapsed }"
    :type="isDisabled ? null : 'button'"
    :aria-current="active ? 'page' : null"
    :aria-disabled="isDisabled ? 'true' : null"
    :title="collapsed ? item.label : null"
    @click="isDisabled ? null : $emit('select', item)"
  >
    <!-- One outline icon in both states: selected, it takes the item's purple. -->
    <span class="hub-nav-item__icon">
      <HubNavIcon :name="item.icon" />
    </span>
    <span class="hub-nav-item__label">{{ item.label }}</span>
    <HubBadge v-if="item.badge && !isDisabled" :count="count" class="hub-nav-item__badge" />
  </component>
</template>

<style scoped>
.hub-nav-item {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  min-height: 40px;
  padding: 0 13px;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: var(--xl-color-on-surface-variant);
  font-family: var(--xl-font-family);
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  text-align: left;
  cursor: pointer;
}

.hub-nav-item__icon {
  display: flex;
  flex-shrink: 0;
}

.hub-nav-item__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hub-nav-item:not(.hub-nav-item--disabled):hover {
  background: var(--xlm-theme-state-layers-light-on-surface-opacity-008, rgba(30, 26, 31, 0.08));
}

.hub-nav-item:not(.hub-nav-item--disabled):active {
  background: var(--xlm-theme-state-layers-light-on-surface-opacity-012, rgba(30, 26, 31, 0.12));
}

.hub-nav-item:focus-visible {
  outline: 2px solid #5e2f78;
  outline-offset: 2px;
}

.hub-nav-item--active {
  background: var(--xl-color-primary-container);
  color: var(--xl-color-primary);
  font-weight: 600;
}

.hub-nav-item--active:hover {
  background: rgba(94, 47, 120, 0.12);
}

/* The tooltip's wrapper takes the row's whole width, as the item does. */
.hub-nav-item__tip {
  display: flex;
  width: 100%;
}

/* Greyed, but still readable: it is telling you what is coming, not hiding it. */
.hub-nav-item--disabled {
  cursor: default;
  opacity: 0.38;
}

/* The icon rail: icon over a small label, which is what the old collapsed
   sidebar did and what keeps 11 items legible in 88px. */
.hub-nav-item--collapsed {
  flex-direction: column;
  gap: 4px;
  padding: 6px 2px;
  border-radius: 12px;
  text-align: center;
}

.hub-nav-item--collapsed .hub-nav-item__label {
  flex: none;
  width: 100%;
  font-size: 10.5px;
  line-height: 14px;
  letter-spacing: -0.02em;
}

.hub-nav-item--collapsed .hub-nav-item__badge {
  position: absolute;
  top: 2px;
  right: 12px;
}

.hub-nav-item--collapsed {
  position: relative;
}
</style>
